import { toProjectRow } from '@/features/projects';
import { CALENDAR_MINE_STATUSES } from '@/shared/constants/statuses';
import type {
  AgendaOccurrence,
  AgendaProject,
  CalendarItem,
  Id,
  ProjectWithContext,
} from '@/shared/types/domain';

/**
 * Os itens da grade — **uma agenda, duas origens de dado**
 * (spec-calendar-ux.md §3.2, [ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md)).
 *
 * ⚠️ **Este arquivo se chamava `sources.ts` e a mudança não é cosmética.** Ele
 * carregava as quatro fontes do calendário (Agenda Fixa · Projetos · Meus
 * Projetos · Propostas), com uma tabela de cor por fonte e um seletor na tela.
 * O modelo caiu inteiro por dois motivos:
 *
 * 1. **"Meus Projetos" não era uma fonte.** As outras três falavam de *firmeza*
 *    (rotina, decidido, proposto) e ela falava de *posse* — dois eixos na mesma
 *    lista, e por isso a pergunta "o que é a agenda oficial e o que é meu?"
 *    nunca teve resposta boa: um projeto aprovado meu é as duas coisas.
 * 2. **A lista de calendários importava uma affordance sem premissa.** No macOS
 *    ela existe porque os eventos vêm de contas diferentes; aqui há uma
 *    procedência só — a igreja.
 *
 * O que sobrou são duas ORIGENS DE DADO, que continuam existindo porque
 * respondem coisas diferentes, e a diferença entre elas é exatamente o que o
 * clique vai poder abrir:
 *
 * | Origem | O que traz | `access` |
 * |---|---|---|
 * | `agenda_projects` (RPC) | a filial inteira, os quatro status vivos | `reduced` |
 * | `select` em `projects` (RLS) | o que a RLS te dá | `full` |
 */

/** Quem cria o item sabe se ele é da pessoa; a tela só pinta. */
export interface MyScope {
  profileId: Id | null;
  /** Vínculo DIRETO de `leader`/`assistant` — nunca `my_ministry_ids()`. */
  ministryIds: readonly Id[];
}

/**
 * "Meu" é vínculo direto de liderança, **ou autoria** (spec-calendar.md §3.2).
 *
 * O `created_by` cobre o caso que o PRD já previu (Feature 2, decisão 1): um
 * `overseer` que cria projeto para um ministério onde não tem vínculo continua
 * enxergando o que acabou de escrever como seu.
 *
 * É a tradução literal da expressão que a RPC calcula no servidor — e as duas
 * existem porque a RPC decide com ela o **colapso de dignidade** (`changes_requested`
 * de terceiro sai como `under_review`), enquanto aqui ela decide o **peso do
 * chip** e quem entra na caixa de entrada. Mesma definição, dois trabalhos.
 *
 * ⚠️ **Ela não decide o que a pessoa alcança.** Isso é da RLS e da RPC, e
 * `isMine` nunca é consultado para revelar nem esconder linha nenhuma.
 */
export function isMine(
  project: Pick<ProjectWithContext, 'ministry_id' | 'created_by'>,
  scope: MyScope,
): boolean {
  return (
    scope.ministryIds.includes(project.ministry_id) ||
    (scope.profileId !== null && project.created_by === scope.profileId)
  );
}

export interface BuildInput {
  /** `agenda_projects` — já recortada e já colapsada pelo servidor. */
  agenda: readonly AgendaProject[];
  /** `projects_with_context` pela RLS: alcance de LEITURA, ainda não "meu". */
  readable: readonly ProjectWithContext[];
  occurrences: readonly AgendaOccurrence[];
  scope: MyScope;
}

/**
 * Um projeto, um item — e **quando as duas origens trazem o mesmo projeto, a
 * linha do `select` vence**.
 *
 * Ela é a verdade mais completa: status verdadeiro (sem o colapso da RPC),
 * todas as colunas, e é ela que faz o clique abrir o projeto inteiro em vez de
 * uma folha de quatro campos. A precedência já existia quando isto atribuía
 * *fonte*; o que mudou é o que ela atribui — `access` e `isMine`.
 *
 * ── ⚠️ O COLAPSO DA RPC E ESTA PRECEDÊNCIA (ADR-015 §4 vs. spec §3.2) ────────
 *
 * A RPC projeta `changes_requested` de terceiro como `under_review`. Aqui, a
 * linha do `select` vence — então **quem tem alcance de leitura amplo (overseer,
 * admin, coordenador dentro da coordenação) volta a ver "Ajustes solicitados"
 * na grade.** Isso foi visto na tela antes de ser escrito, e **não é o colapso
 * furado**:
 *
 * - O colapso protege quem **não tem a linha**. Ele é a fronteira da PROJEÇÃO
 *   REDUZIDA — o que a RPC entrega ALÉM do que a RLS já dá. Para quem a RLS
 *   autoriza, o dado não está sendo revelado: ele já está em `/analise`, na
 *   tela do projeto e na linha do tempo.
 * - Esconder no calendário o que a mesma pessoa lê duas telas adiante criaria
 *   divergência entre a grade e o documento — o tipo de inconsistência que faz
 *   o revisor desconfiar do número, não da regra.
 * - O líder de outro ministério, que é quem a decisão 91 nomeia, **continua
 *   vendo "Em análise"**: a RLS não lhe dá a linha, então só a RPC o alcança.
 *
 * A regra em uma frase: **o servidor decide o que TRAFEGA; a RLS decide quem
 * tem direito à verdade inteira.** As duas concordam por construção.
 *
 * ⚠️ **`CALENDAR_MINE_STATUSES` continua filtrando o lado do `select`, e não é
 * redundante com a RPC.** A RPC já recorta os quatro status vivos, mas o
 * `select` traz o que a RLS der: sem esta linha, um rascunho COM data do próprio
 * ministério entraria na grade pela porta dos fundos — exatamente o veto que a
 * US-12 chama de absoluto.
 */
export function buildCalendarItems({
  agenda,
  readable,
  occurrences,
  scope,
}: BuildInput): readonly CalendarItem[] {
  const byId = new Map<Id, CalendarItem>();

  for (const row of agenda) {
    byId.set(row.id, {
      kind: 'project',
      access: 'reduced',
      // `is_mine` vem do servidor, calculado com o mesmo predicado de `isMine`
      // acima — é ele que a RPC usa para decidir o colapso, então confiar nele
      // aqui mantém as duas pontas concordando sem uma segunda regra escrita.
      isMine: row.is_mine,
      project: toProjectRow(row),
    });
  }

  for (const project of readable) {
    // ⚠️ O recorte de "meu" NÃO é o que decide a entrada aqui — a RLS já
    // decidiu o alcance, e para o coordenador ele é a coordenação inteira.
    // `isMine` só pinta o chip.
    if (!(CALENDAR_MINE_STATUSES as readonly string[]).includes(project.status)) continue;
    if (!project.starts_at || !project.ends_at) continue;

    byId.set(project.id, {
      kind: 'project',
      access: 'full',
      isMine: isMine(project, scope),
      project,
    });
  }

  return [
    ...byId.values(),
    ...occurrences.map(
      (occurrence): CalendarItem => ({
        kind: 'occurrence',
        // A rotina da igreja nunca abre tela nenhuma (spec-fixed-agenda.md §7),
        // e não é de ninguém. Hoje esta lista chega sempre vazia (§3.3).
        access: 'reduced',
        isMine: false,
        occurrence,
      }),
    ),
  ];
}
